// Relatório das Finanças do mês ou do ano, em PDF e em planilha do Excel (para o contador ou para o IR).
import type { Financas, Lancamento } from '../store/types';
import {
  cartoesDe, diferencaPaga, faturasNoPeriodo, valorPagoDe, gastosPorCategoria, limitesMes, meiAno, metasDe, moedaDe, numerosMes, ocorrenciasNoPeriodo, saldoMeta, somaMes,
} from '../store/financas';
import { gerarECompartilharPdf, type BlocoPdf } from './pdf';
import { compartilharXlsx } from './planilha';
import { dinheiro } from './moeda';
import { nomeMes } from './date';
import { t } from './i18n';

export type Periodo = { tipo: 'mes'; ym: string } | { tipo: 'ano'; ano: number };
const br = (d: string) => d.split('-').reverse().join('/');
const r2 = (v: number) => Math.round(v * 100) / 100;

function mesesDo(p: Periodo) {
  if (p.tipo === 'mes') return [p.ym];
  return Array.from({ length: 12 }, (_, i) => `${p.ano}-${String(i + 1).padStart(2, '0')}`);
}
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
export const tituloPeriodo = (p: Periodo) => (p.tipo === 'mes'
  ? `${cap(nomeMes(Number(p.ym.slice(5)) - 1))} ${p.ym.slice(0, 4)}` : String(p.ano));

function dados(d: Financas, p: Periodo, nomeCat: (c: string) => string) {
  const meses = mesesDo(p);
  const porMes = meses.map(ym => ({ ym, ...numerosMes(d, ym) }));
  const itens: Lancamento[] = porMes.flatMap(m => m.itens).sort((a, b) => a.data.localeCompare(b.data));
  const tot = porMes.reduce((s, m) => ({ entrou: s.entrou + m.entrou, saiu: s.saiu + m.saiu, guardou: s.guardou + m.guardou, saldo: s.saldo + m.saldo }),
    { entrou: 0, saiu: 0, guardou: 0, saldo: 0 });
  const cats = [...gastosPorCategoria(itens).entries()].sort((a, b) => b[1] - a[1]).map(([c, v]) => ({ c: nomeCat(c), v: r2(v), pct: tot.saiu ? v / tot.saiu : 0 }));
  const de = limitesMes(meses[0]).de, ate = limitesMes(meses[meses.length - 1]).ate;
  const contas = ocorrenciasNoPeriodo(d.contas, de, ate);
  const faturas = faturasNoPeriodo(d, de, ate);
  const origem = (l: Lancamento) => (l.origem === 'cartao' ? t('Cartão') : l.origem === 'emprestimo' ? t('Financiamento') : l.origem === 'nota' ? t('Nota fiscal') : l.origem === 'trabalho' ? t('Onde trabalho') : '');
  // descontos obtidos e multas e juros pagos (diferença entre o valor pago e o valor da conta)
  const difs = contas.filter(o => o.pago).map(o => ({ o, dif: diferencaPaga(o.conta, o.venc) })).filter(z => z.dif !== 0);
  const descontos = r2(difs.filter(z => z.dif < 0).reduce((s2, z) => s2 - z.dif, 0));
  const encargos = r2(difs.filter(z => z.dif > 0).reduce((s2, z) => s2 + z.dif, 0));
  return { meses, porMes, itens, tot, cats, contas, faturas, origem, difs, descontos, encargos };
}

export async function relatorioPdf(d: Financas, p: Periodo, nomeCat: (c: string) => string) {
  const $ = (v: number) => dinheiro(v, moedaDe(d));
  const x = dados(d, p, nomeCat);
  const titulo = t('Finanças · {p}', { p: tituloPeriodo(p) });
  const blocos: BlocoPdf[] = [
    { t: 'titulo', texto: titulo },
    { t: 'tabela', colunas: [t('Entrou'), t('Saiu'), t('Guardou'), t('Saldo')], linhas: [[$(x.tot.entrou), $(x.tot.saiu), $(x.tot.guardou), $(x.tot.saldo)]] },
  ];
  if (p.tipo === 'ano') blocos.push({ t: 'sub', texto: t('Mês a mês') },
    { t: 'tabela', colunas: [t('Mês'), t('Entrou'), t('Saiu'), t('Guardou'), t('Saldo')], larguras: [0.24, 0.19, 0.19, 0.19, 0.19],
      linhas: x.porMes.map(m => [cap(nomeMes(Number(m.ym.slice(5)) - 1)), $(m.entrou), $(m.saiu), $(m.guardou), $(m.saldo)]) });
  if (x.cats.length) blocos.push({ t: 'sub', texto: t('Gastos por categoria') },
    { t: 'tabela', colunas: [t('Categoria'), t('Valor'), '%'], larguras: [0.5, 0.3, 0.2], linhas: x.cats.map(c => [c.c, $(c.v), `${Math.round(c.pct * 100)}%`]) });
  if (x.descontos || x.encargos) blocos.push({ t: 'tabela', colunas: [t('Descontos obtidos'), t('Multas e juros pagos')], linhas: [[$(x.descontos), $(x.encargos)]] });
  if (x.contas.length) blocos.push({ t: 'sub', texto: t('Contas') },
    { t: 'tabela', colunas: [t('Vencimento'), t('Conta'), t('Valor'), t('Pago'), t('Diferença'), t('Situação')], larguras: [0.13, 0.3, 0.14, 0.14, 0.15, 0.14],
      linhas: x.contas.map(o => {
        const dif = diferencaPaga(o.conta, o.venc);
        return [br(o.venc), (o.conta.tipo === 'receber' ? '+ ' : '') + o.conta.nome, $(o.conta.valor), o.pago ? $(valorPagoDe(o.conta, o.venc)) : '-',
          dif < 0 ? t('desconto {v}', { v: $(-dif) }) : dif > 0 ? t('multa e juros {v}', { v: $(dif) }) : '-',
          o.pago ? (o.conta.tipo === 'pagar' ? t('paga') : t('recebida')) : o.atraso ? t('em atraso') : t('em aberto')];
      }) });
  if (x.faturas.length) blocos.push({ t: 'sub', texto: t('Faturas dos cartões') },
    { t: 'tabela', colunas: [t('Vencimento'), t('Cartão'), t('Valor'), t('Situação')], larguras: [0.18, 0.44, 0.18, 0.2],
      linhas: x.faturas.map(f => [br(f.venc), f.cartao.nome, $(f.total), f.paga ? t('paga') : f.atraso ? t('em atraso') : t('em aberto')]) });
  const metas = metasDe(d);
  if (metas.length) blocos.push({ t: 'sub', texto: t('Metas') },
    { t: 'tabela', colunas: [t('Meta'), t('Guardado'), t('Alvo'), t('Prazo')], larguras: [0.4, 0.2, 0.2, 0.2],
      linhas: metas.map(m => [m.nome, $(saldoMeta(d, m)), $(m.alvo), m.prazo ? br(m.prazo) : '-']) });
  if (d.mei.ativo) {
    const ano = p.tipo === 'ano' ? p.ano : Number(p.ym.slice(0, 4));
    const mei = meiAno(d, ano);
    blocos.push({ t: 'par', texto: t('MEI: faturamento em {ano} de {v} (limite {l}).', { ano, v: $(mei.fat), l: $(d.mei.limite) }) });
  }
  if (x.itens.length) blocos.push({ t: 'sub', texto: t('Lançamentos') },
    { t: 'tabela', colunas: [t('Data'), t('Descrição'), t('Categoria'), t('Valor')], larguras: [0.14, 0.5, 0.18, 0.18],
      linhas: x.itens.map(l => [br(l.data).slice(0, 10), l.desc + (l.origem ? ` (${x.origem(l)})` : ''), l.valor > 0 ? t('Entrada') : nomeCat(l.cat), (l.valor < 0 ? '- ' : '') + $(Math.abs(l.valor))]) });
  await gerarECompartilharPdf(t('Finanças {p}', { p: tituloPeriodo(p) }), blocos, 'My Planner');
}

export async function relatorioXlsx(d: Financas, p: Periodo, nomeCat: (c: string) => string) {
  const x = dados(d, p, nomeCat);
  const abas = [
    { nome: t('Resumo'), colunas: [t('Mês'), t('Entrou'), t('Saiu'), t('Guardou'), t('Saldo')],
      linhas: [...x.porMes.map(m => [m.ym, r2(m.entrou), r2(m.saiu), r2(m.guardou), r2(m.saldo)] as (string | number)[]),
        ...(x.porMes.length > 1 ? [[t('Total'), r2(x.tot.entrou), r2(x.tot.saiu), r2(x.tot.guardou), r2(x.tot.saldo)]] : [])] },
    { nome: t('Lançamentos'), colunas: [t('Data'), t('Descrição'), t('Categoria'), t('Origem'), t('Valor')],
      linhas: x.itens.map(l => [l.data, l.desc, l.valor > 0 && l.cat === 'entrada' ? t('Entrada') : nomeCat(l.cat), x.origem(l), r2(l.valor)]) },
    { nome: t('Categorias'), colunas: [t('Categoria'), t('Valor'), '%'], linhas: x.cats.map(c => [c.c, c.v, Math.round(c.pct * 1000) / 10]) },
    { nome: t('Contas'), colunas: [t('Vencimento'), t('Conta'), t('Tipo'), t('Valor'), t('Valor pago'), t('Desconto'), t('Multa e juros'), t('Situação')],
      linhas: [...x.contas.map(o => { const dif = diferencaPaga(o.conta, o.venc);
          return [o.venc, o.conta.nome, o.conta.tipo === 'pagar' ? t('a pagar') : t('a receber'), o.conta.valor, o.pago ? valorPagoDe(o.conta, o.venc) : '',
            dif < 0 ? -dif : '', dif > 0 ? dif : '', o.pago ? t('quitada') : o.atraso ? t('em atraso') : t('em aberto')]; }),
        ...x.faturas.map(f => [f.venc, t('Fatura {c}', { c: f.cartao.nome }), t('a pagar'), f.total, f.paga ? f.total : '', '', '', f.paga ? t('quitada') : f.atraso ? t('em atraso') : t('em aberto')]),
        ...(x.descontos || x.encargos ? [['', t('Total'), '', '', '', x.descontos, x.encargos, ''] as (string | number)[]] : [])] },
  ];
  if (cartoesDe(d).length) {
    const ini = x.meses[0];
    abas.push({ nome: t('Cartões'), colunas: [t('Cartão'), t('Data da compra'), t('Descrição'), t('Parcela'), t('Fatura'), t('Valor')],
      linhas: x.itens.filter(l => l.origem === 'cartao').map(l => {
        const [, cid] = l.id.split(':');
        const cp = (d.compras ?? []).find(c => c.id === cid);
        const c = cartoesDe(d).find(k => k.id === cp?.cartao);
        return [c?.nome ?? '', cp?.data ?? '', cp?.desc ?? l.desc, cp && cp.parcelas > 1 ? `${Number(l.id.split(':')[2]) + 1}/${cp.parcelas}` : '', cp ? somaMes(cp.fatura1, Number(l.id.split(':')[2])) : ini, r2(-l.valor)];
      }) });
  }
  await compartilharXlsx(t('Finanças {p}', { p: tituloPeriodo(p) }), abas);
}
